import ImageCarousel from "../../components/image-carousel/image-carousel";
import Parallax from "../../components/parallax/parallax";
import { useEffect, useState, useCallback, useRef } from "react";
import { getImageUrls } from "../../utils/getImageUrls";
import "./our-story.scss";
import LoadingOverlay from "../../components/loading/LoadingOverlay";

export default function OurStory() {
  const BEGINNING_TITLE = "The Beginning";

  const BEGINNING_PARAGRAPH_ONE =
    "Our love story began quietly– there were no fireworks (yet), no instant romance– just a simple connection that turned into a steady friendship. We met at a Catholic youth group camp in 2009 and our friendship began–we’d cross paths at community events, serve in music ministry together, share quick conversations, then continue with our own routines.";

  const BEGINNING_PARAGRAPH_TWO =
    "For years, life carried on, but we never fully drifted apart. It wasn’t until 2017 that the friendship that once lived in the background became more present. We supported each other, challenged one another, annoyed each other, and continued to share pieces of life, never quite seeing what was quietly taking shape between us.";

  const FALLING_IN_LOVE_TITLE = "Falling in Love";

  const FALLING_IN_LOVE_PARAGRAPH_ONE =
    "The turning point in our strictly platonic friendship came unexpectedly, after a conversation with a mutual friend about relationships and non-negotiables in a partner. He shared an article written by a seminarian, a reflection on love that shifted our own perspectives and approach to love. It moved us beyond the pursuit of someone perfectly compatible, and towards a deeper love found when two people discover and learn how to complement one another.";

  const FALLING_IN_LOVE_PARAGRAPH_TWO =
    "And that’s when we began to see each other in a different light, and began our new chapter.";

  const FALLING_IN_LOVE_PARAGRAPH_THREE =
    "Chris asked Ella to be his girlfriend on November 9, 2019.";

  const FALLING_IN_LOVE_PARAGRAPH_FOUR =
    "Looking back, the simple friendship that started years ago laid the foundation for the love that would follow. A love that carried us through changing seasons, new beginnings, all the steady moments in between, and into the life we’re building now.";

  const PROPOSAL_TITLE = "The Proposal";

  const PROPOSAL_PARAGRAPH_ONE =
    "Ella wanted to plan a trip (as she always does), and found a deal (as she always does) on flights to Germany. But she definitely didn’t plan what would happen next.";

  const PROPOSAL_PARAGRAPH_TWO =
    "Ella’s notorious bad luck during trips has rubbed off on Chris over the years— and this trip was no exception. His secret photographer cancelled on him the day before, Chris would find out the replacement she recommended could only speak Russian and German, and communicating the proposal location and plans became a challenge.";

  const PROPOSAL_PARAGRAPH_THREE =
    "But of course, there is always a happy ending after the stroke of bad luck that befalls them!";

  const PROPOSAL_PARAGRAPH_FOUR =
    "The sun was shining, the skies were blue, and it was a beautiful day with the beautiful backdrop of Dresden, Germany. And like many of their adventures and favourite days spent together, Chris and Ella found themselves walking by the water, where Chris would ask the most important question of his life.";

  const PROPOSAL_PARAGRAPH_FIVE =
    "And on March 16th, 2025, Ella said “yes” to marrying the love of her life.";

  const NEXT_CHAPTER_TITLE = "The Next Chapter";

  const NEXT_CHAPTER_PARAGRAPH_ONE =
    "As we prepare to say I do, we’re grateful for every twist, turn, and moment that brought us here. Surrounded by the people who have been with us through our different seasons and added to our own individual stories, we can’t wait to celebrate— not just our wedding day, but the love that has shaped us and has shown us how to love.";

  const NEXT_CHAPTER_PARAGRAPH_TWO =
    "We’re excited to see how our story continues to unfold.";

  const proposalImage = {
    src: `${process.env.PUBLIC_URL}/images/pictures/ChrisEllaEngagement-04.webp`,
    alt: "Proposal Picture",
  };

  //imageUrls
  const [imageUrls, setImageUrls] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    getImageUrls("our-story").then((urls) => {
      setImageUrls(urls);

      const initial = {};
      for (let i = 0; i < urls.length; i += 2) {
        initial[`our-story-${i}`] = false;
      }
    });
  }, []);

  useEffect(() => {
    if (imageUrls.length > 10) {
      const timeoutId = setTimeout(() => {
        setImagesLoaded(true);
      }, 1500);
      return () => clearTimeout(timeoutId);
    }
  });

  return (
    <>
      <LoadingOverlay isLoading={!imagesLoaded} />
      <div className={`our-story ${imagesLoaded ? "fade-in" : ""}`}>
        <section className="our-story-beginning ">
          <Parallax className="beginning-bg" speed={-0.07} />
          <div className="our-story-container beginning">
            <h1 className="cormorant-400 font-size-8 letter-spacing-6 position-relative margin-0 text-medium">
              OUR STORY
            </h1>
            <h2 className="cormorant-italic-500 font-size-6 position-relative margin-top-10 margin-bottom-4">
              {BEGINNING_TITLE}
            </h2>
            <p>{BEGINNING_PARAGRAPH_ONE}</p>
            <p>{BEGINNING_PARAGRAPH_TWO}</p>
          </div>
        </section>

        <section className="our-story-falling-in-love">
          <div className="our-story-container falling-in-love">
            <h2 className="cormorant-italic-500 font-size-6 position-relative margin-top-10 margin-bottom-4">
              {FALLING_IN_LOVE_TITLE}
            </h2>
            <p>{FALLING_IN_LOVE_PARAGRAPH_ONE}</p>
            <p>{FALLING_IN_LOVE_PARAGRAPH_TWO}</p>
            <p>{FALLING_IN_LOVE_PARAGRAPH_THREE}</p>
            <p>{FALLING_IN_LOVE_PARAGRAPH_FOUR}</p>
          </div>
          <div className="carousel-container">
            <div className="mobile-hidden">
              <ImageCarousel
                images={imageUrls}
                speed={0.8}
                className={"carousel-250"}
                spacing={1.5}
                slideHeight={300}
              />
            </div>
            <div className="mobile-show">
              <ImageCarousel
                images={imageUrls}
                speed={1.2}
                className={"carousel-250"}
                spacing={1}
                slideHeight={300}
              />
            </div>
          </div>
        </section>

        <section className="our-story-proposal proposal-bg">
          <div className="proposal-bg-space"></div>
          <div className="proposal-img-mobile">
            <img
              src={proposalImage.src}
              alt={proposalImage.alt}
              loading="lazy"
            />
          </div>

          <div className="our-story-container proposal">
            <div className="proposal-img mobile-hidden">
              <img
                src={proposalImage.src}
                alt={proposalImage.alt}
                loading="lazy"
              />
            </div>
            <div className="proposal-text">
              <h2 className="cormorant-italic-500 font-size-6 position-relative margin-top-10 margin-bottom-4">
                {PROPOSAL_TITLE}
              </h2>
              <p>{PROPOSAL_PARAGRAPH_ONE}</p>
              <p>{PROPOSAL_PARAGRAPH_TWO}</p>
              <p>{PROPOSAL_PARAGRAPH_THREE}</p>
              <p>{PROPOSAL_PARAGRAPH_FOUR}</p>
              <p>{PROPOSAL_PARAGRAPH_FIVE}</p>
            </div>
          </div>
        </section>

        <section className="our-story-next-chapter next-chapter-bg">
          <div className="next-chapter-parallax-container">
            <Parallax className="next-chapter-parallax" speed={-0.08} />

            <div className="our-story-container next-chapter">
              <div className="our-story-next-chapter-content display-flex flex-column align-center text-center">
                <h2 className="cormorant-italic-500 font-size-6 position-relative margin-top-10 margin-bottom-4">
                  {NEXT_CHAPTER_TITLE}
                </h2>
                <p>{NEXT_CHAPTER_PARAGRAPH_ONE}</p>
                <p>{NEXT_CHAPTER_PARAGRAPH_TWO}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
